import { useEffect, useState } from "react";
import { http, apiError } from "@/lib/api";
import { ThumbsUp, ChatCircleText } from "@phosphor-icons/react";

export default function ClientLogs() {
  const [logs, setLogs] = useState([]);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await http.get("/client/workout-logs");
        setLogs(data);
      } catch (e) { setErr(apiError(e)); }
    })();
  }, []);

  return (
    <div className="p-6 md:p-10 max-w-4xl mx-auto">
      <div className="id-label mb-3">— Training Log —</div>
      <h1 className="id-h1 mb-10">Sessions</h1>

      {err && <div className="border border-[#FF3333] bg-[#FF3333]/10 px-4 py-3 text-sm text-[#FF3333] mb-6">{err}</div>}

      {logs.length === 0 ? (
        <div className="id-card text-center py-16" data-testid="empty-logs">
          <p className="text-zinc-400 font-manrope">No sessions logged yet. Log a workout from your program page.</p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="logs-list">
          {logs.map((l) => (
            <div key={l.id} className="id-card" data-testid={`log-${l.id}`}>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="font-oswald text-xl uppercase tracking-wide flex items-center gap-2">
                    {l.day_name}
                    {typeof l.week_number === "number" && l.week_number > 0 && (
                      <span className="text-[10px] font-manrope bg-[#262626] text-zinc-300 border border-[#262626] px-1.5 py-0.5" data-testid={`log-week-${l.id}`}>
                        Wk {l.week_number}
                      </span>
                    )}
                  </div>
                  <div className="id-label text-zinc-500">{new Date(l.logged_at).toLocaleString()}</div>
                </div>
                <span className="id-label text-zinc-500">{l.exercises.length} exercises</span>
              </div>
              <ul className="divide-y divide-[#262626]">
                {l.exercises.map((ex, i) => (
                  <li key={i} className="py-2 text-sm font-manrope">
                    <div className="font-semibold mb-1">{ex.name}</div>
                    <div className="flex flex-wrap gap-2">
                      {ex.sets.map((s, j) => (
                        <span key={j} className="id-label text-zinc-400 border border-[#262626] px-2 py-1">
                          {s.reps} × {s.weight}kg
                        </span>
                      ))}
                      {ex.sets.length === 0 && <span className="text-zinc-600 text-xs">(no sets logged)</span>}
                    </div>
                    {ex.notes && (
                      <div className="text-xs font-manrope text-zinc-400 mt-1.5 pl-2 border-l-2 border-[#262626]">
                        {ex.notes}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
              {l.notes && <p className="mt-3 pt-3 border-t border-[#262626] text-zinc-400 text-sm font-manrope">{l.notes}</p>}
              {(l.coach_comment || l.coach_kudos) && (
                <div className="mt-3 pt-3 border-t border-[#FF3333]/40" data-testid={`coach-feedback-${l.id}`}>
                  <div className="id-label text-[#FF3333] mb-1 flex items-center gap-2">
                    {l.coach_kudos && <ThumbsUp size={12} weight="fill" />} — From Coach —
                  </div>
                  {l.coach_comment && (
                    <p className="text-zinc-200 text-sm font-manrope whitespace-pre-wrap flex items-start gap-2">
                      <ChatCircleText size={14} weight="fill" className="text-[#FF3333]/70 mt-0.5 shrink-0" />
                      <span>{l.coach_comment}</span>
                    </p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
